import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { EmployeeViewDirectoryFilters } from "../../components/features/employee-view-directory/components/employee-view-directory-filters.tsx";
import { EmployeeViewDirectoryGrid } from "../../components/features/employee-view-directory/components/employee-view-directory-grid.tsx";
import { useEmployeeViewDirectory } from "../../components/features/employee-view-directory/hooks/use-employee-view-directory.ts";
import type { EmployeeDirectoryFilters } from "../../components/features/employee-view-directory/types.ts";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useEmployeeNameOptions } from "../../hooks/use-employee-name-options.ts";
import { useSqeEmployeeNameOptions } from "../../hooks/use-sqe-employee-name-options.ts";
import type { PageProps } from "../../types/page.ts";
import { formatNavigationLabel } from "../../utils/sqe-labs-navigation-access.ts";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";
import { showError } from "../../utils/toast.ts";
import styles from "./employee-view-directory-page.module.css";

const EMPTY_FILTERS: EmployeeDirectoryFilters = { employeeId: null };

export default function EmployeeViewDirectoryPage({ title, pageMeta }: PageProps) {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const isSqeLabs = useSqeLabs();
  const regularEmployeeNames = useEmployeeNameOptions(!isSqeLabs);
  const sqeEmployeeNames = useSqeEmployeeNameOptions(isSqeLabs);
  const employeeNames = isSqeLabs ? sqeEmployeeNames : regularEmployeeNames;
  const [employeeId, setEmployeeId] = useState("");
  const [filters, setFilters] = useState<EmployeeDirectoryFilters>(EMPTY_FILTERS);
  const directory = useEmployeeViewDirectory(filters);
  const loadMoreObserver = useRef<IntersectionObserver | null>(null);
  const { hasMore, loadMore } = directory;
  const pageTitle = formatNavigationLabel(
    pageMeta?.title || title || "Employee View Directory",
    isSqeLabs ? "SQELABS" : "",
  ) ?? "Employee View Directory";

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [pageTitle, setBreadcrumbs]);

  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    loadMoreObserver.current?.disconnect();
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    }, { rootMargin: "240px" });
    observer.observe(node);
    loadMoreObserver.current = observer;
  }, [hasMore, loadMore]);

  useEffect(() => () => loadMoreObserver.current?.disconnect(), []);

  useEffect(() => {
    if (employeeNames.error) {
      showError(employeeNames.error, { id: "employee-directory-name-options-error" });
    }
  }, [employeeNames.error]);

  useEffect(() => {
    if (directory.error) {
      showError(directory.error, { id: "employee-directory-list-error" });
    }
  }, [directory.error]);

  const lookupLoading = employeeNames.loading;

  return <PageShell className={styles.page}>
    <EmployeeViewDirectoryFilters
      employeeId={employeeId}
      employeeOptions={employeeNames.options}
      loading={lookupLoading || directory.loading}
      onEmployeeChange={setEmployeeId}
      onApply={() => setFilters({ employeeId: employeeId ? Number(employeeId) : null })}
      onClear={() => { setEmployeeId(""); setFilters(EMPTY_FILTERS); }}
    />
 
    <EmployeeViewDirectoryGrid
      employees={directory.employees} loading={directory.loading} loadingMore={directory.loadingMore}
      loadMoreRef={loadMoreRef}
      onEmployeeClick={(employee) => navigate("/dashboard/yourColleague/details", { state: { employee } })}
    />
  </PageShell>;
}
